import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Typography,
  notification,
} from "antd";
import { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useMutation, useQuery } from "react-query";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IRankedSeasonAnnouncementService } from "@shared/interfaces/ranked-season-announcement-service.interface";
import {
  RankedSeasonAnnouncementDto,
  UpdateRankedSeasonAnnouncementDto,
} from "@shared/types/ranked-season-announcement/ranked-season-announcement.dto";
import { extractErrorMessage } from "@shared/utils/error-message";
import {
  PACIFIC_DATETIME_FORMAT,
  parseUtcIsoToPacificDayjs,
  pacificDayjsToUtcIso,
} from "@shared/utils/pacific-datetime";

type AnnouncementFormValues = {
  title?: string;
  body?: string;
  endsAt?: Dayjs | null;
  isActive: "1" | "0";
};

const RankedSeasonAnnouncementPage = () => {
  const [form] = Form.useForm<AnnouncementFormValues>();
  const announcementService = appInject<IRankedSeasonAnnouncementService>(
    DI_TOKENS.rankedSeasonAnnouncementService,
  );

  const getQuery = useQuery<RankedSeasonAnnouncementDto | null>(
    ["ranked-season-announcement"],
    () => announcementService.get(),
    { refetchOnMount: true },
  );

  const updateMutation = useMutation(
    (dto: UpdateRankedSeasonAnnouncementDto) => announcementService.update(dto),
    {
      onSuccess: (result) => {
        const description = result.jsonRemoved
          ? "S3 JSON removed (announcement inactive)"
          : result.uploadedFileUrl
            ? `Published: ${result.uploadedFileUrl}`
            : undefined;
        notification.success({
          message: "Announcement updated",
          description,
        });
        void getQuery.refetch();
      },
      onError: (error: unknown) => {
        notification.error({
          message: "Failed to update announcement",
          description: extractErrorMessage(
            error,
            "Failed to update announcement",
          ),
        });
      },
    },
  );

  useEffect(() => {
    const announcement = getQuery.data;
    if (announcement) {
      form.setFieldsValue({
        title: announcement.title ?? undefined,
        body: announcement.body ?? undefined,
        endsAt: announcement.endsAt
          ? parseUtcIsoToPacificDayjs(announcement.endsAt)
          : null,
        isActive: announcement.isActive ? "1" : "0",
      });
      return;
    }
    form.setFieldsValue({
      title: undefined,
      body: undefined,
      endsAt: null,
      isActive: "0",
    });
  }, [form, getQuery.data]);

  const handleFinish = (values: AnnouncementFormValues) => {
    updateMutation.mutate({
      title: values.title?.trim() || null,
      body: values.body?.trim() || null,
      endsAt: pacificDayjsToUtcIso(values.endsAt ?? null),
      isActive: values.isActive === "1",
    });
  };

  return (
    <Layout.Content style={{ padding: 24 }}>
      <Typography.Title level={3}>Season-end announcement</Typography.Title>
      <Typography.Paragraph type="secondary">
        Copy and countdown for the Ranked tab teaser. Tier prize amounts are
        edited on each tier. Saving republishes public S3 JSON
        (uploads/rankedseasonannouncement.json) for the app — no Nest client
        GET.
      </Typography.Paragraph>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        style={{ maxWidth: 720 }}
      >
        <Form.Item name="title" label="Title (countdown label)">
          <Input placeholder="e.g. SEASON 0 ENDS IN" />
        </Form.Item>

        <Form.Item name="body" label="Body">
          <Input.TextArea
            rows={4}
            placeholder="Main announcement paragraph shown above prizes"
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="endsAt"
              label="Ends at (countdown, PST)"
              extra="Date and time are entered and shown in Pacific Time (PST/PDT)."
            >
              <DatePicker
                showTime
                style={{ width: "100%" }}
                format={PACIFIC_DATETIME_FORMAT}
                placeholder="Select date and time in PST"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="isActive"
              label="Active"
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="1">Yes</Select.Option>
                <Select.Option value="0">No</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateMutation.isLoading}
          >
            Save
          </Button>
        </Space>
      </Form>
    </Layout.Content>
  );
};

export default RankedSeasonAnnouncementPage;
